import React, {FC} from 'react';
import styled from 'styled-components';

import {AsyncStatusesEnum} from '../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ChannelModel} from '../../../libs/shared/core/src/models/api/channelModel';
import {MessageModel} from '../../../libs/shared/core/src/models/api/messageModel';
import {CellsContext} from '../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {EmlPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {AsyncLoader} from '../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {center, stretch} from '../../../libs/shared/ui/src/styles/mixins';
import {ExpandedDarkThemeProvider} from '../../../libs/shared/ui/src/theme/expandedDarkThemeProvider';
import {EmlSource} from '../../store/panels/eml/emlPanelState';
import {Frame} from '../common/frames/frame';
import {MessageViewerContainer} from '../inboxes/viewer/activity/message/messageViewerContainer';
import {useMessagePrintHandler} from './popupPrintHelpers';

/*
 * Props.
 */

interface PopupEmlProps {
  panelId: EmlPanelId;
  source: EmlSource;
  status: AsyncStatusesEnum;
  message: MessageModel | undefined;
  cellsContext: CellsContext;
  replyChannel: ChannelModel | undefined;
  onRetryClick: () => void;
  onRequestClose: () => void;
  timelineVariation: TimelineVariationsEnum;
}

const StyledFrameDiv = styled.div`
  flex: 1;
  min-height: 0;
  flex-direction: column;
  ${stretch()}
`;

const StyledLoaderDiv = styled.div`
  ${center()}
`;

/*
 * Component.
 */

export const PopupEml: FC<PopupEmlProps> = (props) => {
  const onRequestPrint = useMessagePrintHandler(props.message, props.source);
  return (
    <ExpandedDarkThemeProvider>
      <Frame
        minWidth="540px"
        width={740}
        height={600}
        shouldForcePopout
        onRequestClose={props.onRequestClose}
        onRequestPrint={onRequestPrint}
        startHeight={undefined}
        interactionId="popup-viewer-eml"
      >
        <StyledFrameDiv>{renderContent(props)}</StyledFrameDiv>
      </Frame>
    </ExpandedDarkThemeProvider>
  );
};

function renderContent(props: PopupEmlProps) {
  const {status, onRetryClick, message} = props;

  // Loading / Error states.
  if (status !== AsyncStatusesEnum.LOADED || !message) {
    return (
      <StyledLoaderDiv>
        <AsyncLoader status={status} onRetryClick={onRetryClick} />
      </StyledLoaderDiv>
    );
  }

  // Loaded state.
  return (
    <MessageViewerContainer
      panelId={props.panelId}
      messageUid={message.uid}
      cellsContext={props.cellsContext}
      namespace="global"
      conversationId={undefined}
      conversationActionContext={undefined}
      isSpam={false}
      activityId={undefined}
      previousMessageUid={undefined}
      replyChannel={props.replyChannel}
      emlSource={props.source}
      isAuthorReplying={undefined}
      isSharedDraft={undefined}
      timelineVariation={props.timelineVariation}
    />
  );
}
